import {HttpResponse, HttpService} from "./http.service";
import {ManuscriptModel} from "../models/manuscript.model";
import {TagModel} from "../models/tag.model";
import {ManuscriptDetailsModel} from "../models/manuscript-details.model";
import {CategoryModel} from "../models/category.model";
import {ProposalModel} from "../models/proposal.model";
import {SubmittedModel} from "../models/submitted.model";
import {PaginationModel} from "../models/pagination.model";
import {BookViewModel} from "../models/book-view.model";

export const ManuscriptService = {

    async addManuscript(
        title: string,
        categories: CategoryModel[],
        tags: string[] | TagModel[],
        description: string,
        summary: string,
        partOfManuscript: string,
        typeOfNarration: string,
        realLocation: string,
        fictionLocation: string,
        actionTime: string,
        readerAge: string,
        readerGender: string,
        readerType: string,
        readerAddInfo: string,
        mainCategory: string,
        publisherAlias: string
    ): Promise<HttpResponse<ManuscriptModel>> {
        const tagsReady: TagModel[] = [];
        tags.forEach((tag) => {
            if (typeof tag === 'string') {
                tagsReady.push({
                    id: null,
                    name: tag,
                    tag_type: 'tag'
                });
            } else {
                tagsReady.push(tag);
            }
        });

        this.addToTags(tagsReady, typeOfNarration, 'typeOfNarration');
        this.addToTags(tagsReady, realLocation, 'realLocation');
        this.addToTags(tagsReady, fictionLocation, 'fictionLocation');
        this.addToTags(tagsReady, actionTime, 'actionTime');
        this.addToTags(tagsReady, readerAge, 'readerAge');
        this.addToTags(tagsReady, readerGender, 'readerGender');
        this.addToTags(tagsReady, readerType, 'readerType');
        this.addToTags(tagsReady, readerAddInfo, 'readerAddInfo');

        const response: HttpResponse<ManuscriptModel> = await HttpService.post('manuscript/', {
            title,
            description,
            summary,
            categories: categories.map((category) => {
                return {
                    id: category.id,
                    code: (category.code.indexOf('_GENERALCATEGORY') !== -1) ? category.parent_code : category.code
                }
            }),
            tags: tagsReady,
            fragment: partOfManuscript,
            main_category: mainCategory,
            publisher_alias: publisherAlias
        });

        return response;
    },
    async editManuscript(
        id: string,
        title: string,
        categories: CategoryModel[],
        tags: string[] | TagModel[],
        description: string,
        summary: string,
        partOfManuscript: string,
        typeOfNarration: string,
        realLocation: string,
        fictionLocation: string,
        actionTime: string,
        readerAge: string,
        readerGender: string,
        readerType: string,
        readerAddInfo: string,
        mainCategory: string,
        availableForMarket: boolean
    ): Promise<HttpResponse<ManuscriptModel>> {
        const tagsReady: TagModel[] = [];
        tags.forEach((tag) => {
            if (typeof tag === 'string') {
                tagsReady.push({
                    id: null,
                    name: tag,
                    tag_type: 'tag'
                });
            } else {
                tagsReady.push(tag);
            }
        });

        this.addToTags(tagsReady, typeOfNarration, 'typeOfNarration');
        this.addToTags(tagsReady, realLocation, 'realLocation');
        this.addToTags(tagsReady, fictionLocation, 'fictionLocation');
        this.addToTags(tagsReady, actionTime, 'actionTime');
        this.addToTags(tagsReady, readerAge, 'readerAge');
        this.addToTags(tagsReady, readerGender, 'readerGender');
        this.addToTags(tagsReady, readerType, 'readerType');
        this.addToTags(tagsReady, readerAddInfo, 'readerAddInfo');

        const response: HttpResponse<ManuscriptModel> = await HttpService.patch(`manuscript/${id}/`, {
            title,
            description,
            summary,
            categories: categories.map((category) => {
                return {
                    id: category.id,
                    code: (category.code.indexOf('_GENERALCATEGORY') !== -1) ? category.parent_code : category.code
                }
            }),
            tags: tagsReady,
            fragment: partOfManuscript,
            main_category: mainCategory,
            available_for_manuscript_market: availableForMarket
        });

        return response;
    },
    async uploadFilesManuscript(
        file: File,
        shortFile: File,
        cover: File,
        illustration: File,
        id: string,
        onProgress: Function
    ): Promise<ManuscriptModel> {
        return new Promise((resolve, reject) => {
            const bodyFormData = new FormData();
            if (file !== null) {
                bodyFormData.set('content_full', file);
            }

            if (shortFile !== null) {
                bodyFormData.set('content_fragment', shortFile);
            }

            if (cover !== null) {
                bodyFormData.set('cover', cover);
            }

            if (illustration !== null) {
                bodyFormData.set('illustration', illustration);
            }

            const xhr = new XMLHttpRequest();
            xhr.responseType = 'json';

            xhr.upload.addEventListener("progress", function (evt) {
                if (evt.lengthComputable) {
                    onProgress(Math.floor(evt.loaded / evt.total * 1000) / 10);
                }
            }, false);

            xhr.onload = () => {
                if (xhr.response) {
                    resolve(xhr.response);
                }
            };

            xhr.open('PATCH', process.env.REACT_APP_API + `manuscript/${id}/files/`);
            xhr.setRequestHeader('Authorization', 'Bearer ' + localStorage.getItem('Token'));

            xhr.send(bodyFormData);
        });
    },
    async getManuscript(
        id: string
    ): Promise<ManuscriptDetailsModel> {
        const response: HttpResponse<ManuscriptDetailsModel> = await HttpService.get(`manuscript/${id}/`);

        return response.data;
    },
    async getProposals(id: string, offset: number, limit: number, status: string[]): Promise<ProposalModel[]> {
        let statusUrl = '';
        status.forEach((stat) => {
            statusUrl += '&status=' + stat;
        });

        const response: HttpResponse<PaginationModel<ProposalModel[]>> = await HttpService.get(`manuscript/${id}/proposals/?offset=${offset}&limit=${limit}&ordering=-matching${statusUrl}`);

        return response?.data?.results || [];
    },
    async getSubmitted(id: string): Promise<SubmittedModel[]> {
        const response: HttpResponse<PaginationModel<SubmittedModel[]>> = await HttpService.get(`manuscript/${id}/submitted/?offset=0&limit=-1`);

        return response?.data?.results || [];
    },
    async submitToPublisher(id: number): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`application/${id}/submit/`, {});

        return response.status;
    },
    async confirmPublisher(id: number): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`application/${id}/confirm_publisher_request/`, {});

        return response.status;
    },
    async declinePublisher(id: number): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`application/${id}/decline_publisher_request/`, {});

        return response.status;
    },
    async deleteManuscript(id: string): Promise<any> {
        const response: HttpResponse<any> = await HttpService.post(`manuscript/${id}/close/`, {});

        return response.status;
    },
    async getBasicPreview(id: string): Promise<BookViewModel> {
        const response: HttpResponse<BookViewModel> = await HttpService.get(`manuscript/${id}/basic_preview/`);

        return response.data;
    },
    async getNote(id: string): Promise<{book_note: string}> {
        const response: HttpResponse<{book_note: string}> = await HttpService.get(`manuscript/${id}/note/`);

        return response.data;
    },
    addToTags(tags: TagModel[], addValue: string, type: string) {
        if (addValue) {
            tags.push({
                id: null,
                name: addValue,
                tag_type: type
            });
        }
    }
}
